import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'semantic-ui-react'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import { ReportingQuery } from '~/QueryHooks/ReportingQuery'
import i18n from '~/components/i18n'
import ExcelExport from '~/components/ExcelExport'
import dayjs from 'dayjs'

export type FlagGasEmissionsPeriod = { periodFrom: string; periodTo: string; statusLabel: string }

const getTableColumnHeaders = () => [
  { name: i18n.t('Supplier No.'), key: 'supplierNo', sorted: 'desc' },
  { name: i18n.t('Name/Firmenname'), key: 'companyName', sorted: 'desc' },
  {
    name: i18n.t('Abgeschlossen um'),
    key: 'completedAt',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    sorted: 'desc'
  },
  {
    name: 'THG Emissionen (kg CO<sub>2</sub>-Äq./Kuh u. Jahr)',
    key: 'soldMilkEconomicAllocationFpcmIdf',
    type: 'html-header',
    decimalScale: 2,
    sorted: 'desc'
  },
  { name: i18n.t('Gas mixed sum'), key: 'gasMixedSum', type: 'no-unit-number', sorted: 'desc' },
  { name: i18n.t('Gas CO2 sum'), key: 'gasCo2Sum', type: 'html-header', sorted: 'desc' },
  { name: i18n.t('Gas N2O sum'), key: 'gasN2OSum', type: 'html-header', sorted: 'desc' },
  { name: i18n.t('Gas CH4 sum'), key: 'gasCh4Sum', type: 'html-header', sorted: 'desc' },
  { name: i18n.t('Flag sum'), key: 'flagSum', type: 'no-unit-number', sorted: 'desc' },
  { name: i18n.t('Non-flag sum'), key: 'nonFlagSum', type: 'no-unit-number', sorted: 'desc' }
]

const GassEmissions: React.FC = () => {
  const { t } = useTranslation()

  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 20,
    search: '',
    periodFrom: '',
    periodTo: ''
  })
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getTableColumnHeaders())

  const {
    data: settingsData,
    isLoading: isLoadingSettings
  } = ReportingQuery.getFlagGasEmissionsSettings()

  useEffect(() => {
    if (settingsData) {
      setDataFiltered({
        ...dataFiltered,
        periodFrom: settingsData.periods?.[0]?.periodFrom || '',
        periodTo: settingsData.periods?.[0]?.periodTo || ''
      })
    }
  }, [settingsData])

  const {
    data: indexData,
    isLoading: isLoadingIndex,
    isFetching: isFetchingIndex
  } = ReportingQuery.getFlagGasEmissionsIndex({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  }, !!dataFiltered.periodFrom)

  const rows = indexData?.rows ?? []
  const total = indexData?.filtered ?? 0

  function renderFilters() {
    const periods: FlagGasEmissionsPeriod[] = settingsData?.periods ?? []
    return (
      <>
        <Dropdown
          selection
          placeholder={t('Period')}
          style={{ minWidth: 220 }}
          selectOnBlur
          onChange={(_, { value }) => {
            const periods = value.toString().split('/')
            setDataFiltered({
              ...dataFiltered,
              periodFrom: periods[0],
              periodTo: periods[1]
            })
          }}
          value={dataFiltered?.periodFrom ? `${dataFiltered?.periodFrom}/${dataFiltered?.periodTo}` : ''}
          options={periods?.map(period => ({
            text: `${dayjs(period.periodFrom).format('DD.MM.YYYY')} - ${dayjs(period.periodTo).format('DD.MM.YYYY')}`,
            value: `${period.periodFrom}/${period.periodTo}`
          })) || []}
        />
      </>
    )
  }

  function renderActions() {
    return (
      <ExcelExport
        url={'reporting/co2-footprint/flag-gas-emissions/export/pdf'}
        fileName={'Export'}
        data={{
          ...dataFiltered,
          page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
        }}
        downloadWithFileName
        buttonType={'icon'}
        icon={'file pdf outline'}
        name={t('Export')}
      />
    )
  }

  function renderTable() {
    return (
      <TableComponent
        isFetchingData={isFetchingIndex}
        animated
        tableColumnHeaders={tableColumnHeaders}
        dataFiltered={dataFiltered}
        handleChangeDataFiltered={(data, headers) => {
          setDataFiltered(data)
          if (headers) setTableColumnHeaders(headers)
        }}
        rows={rows}
        total={total}
      />
    )
  }

  const isLoading = isLoadingSettings || isLoadingIndex

  return (
    <Layout
      title={t('Flag/Gas-Emissionen')}
      isCo2Loader
      isBusy={isLoading}
      segmented
      actions={renderActions()}
      filters={renderFilters()}
    >
      {!isLoading && renderTable()}
    </Layout>
  )
}

export default GassEmissions
